/**
 * Creos (07 Workshop, Creos · The page and its states, 8 Oct): what Creo can
 * make for the brand, ready to start. One titled row per category (set on each
 * skill and agent in the CMS; none falls under Other, last); a row shows four
 * (three on a phone) and See all opens the rest in place (Show fewer folds
 * it). Search narrows every row to its matches. Featured cards lead the new
 * chat and the dashboard instead, not this page. A card starts a new chat with
 * its pill in the composer; ⓘ opens its pop-up, whose Use it does the same.
 */
import { useState } from "react";
import { ChevronDown, SearchX } from "lucide-react";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { EmptyState, ErrorState } from "@/app/routes/dashboard/components/ViewStates";
import { WallSearch } from "@/app/routes/dashboard/components/WallBar";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { usePermissions } from "@/hooks/usePermissions";
import type { WorkshopQuickCreateItem } from "@/types/workshop.type";
import { useWorkshopQuickCreate } from "../api/useWorkshopQuickCreate";
import { creoStreams, matchQuickCreate, pickId } from "../quickCreate";
import { CreoCard, CreoCardSkeleton } from "./QuickCreateParts";
import WorkshopQuickCreateDetail from "./WorkshopQuickCreateDetail";
import { useWorkshopDesk } from "./workshopDesk";

export default function WorkshopCreosPage() {
  const desk = useWorkshopDesk();
  const { isGuest, canUseWorkshop } = usePermissions();
  const isPhone = useMediaQuery("(max-width: 767px)");
  const list = useWorkshopQuickCreate(desk.brandId);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [chosen, setChosen] = useState<WorkshopQuickCreateItem | null>(null);

  const items = list.data?.items ?? [];
  const q = query.trim();
  const matches = q ? matchQuickCreate(items, q) : items;
  const streams = creoStreams(matches);
  const rowSize = isPhone ? 3 : 4;
  const brand = desk.brandName ?? "this brand";
  const tap = isPhone ? "Tap" : "Click";

  const toggle = (stream: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(stream)) next.delete(stream);
      else next.add(stream);
      return next;
    });

  const start = (item: WorkshopQuickCreateItem, projectId: string | null = desk.pickedProjectId) =>
    desk.startQuickCreate(item, projectId);

  const card = (item: WorkshopQuickCreateItem, variant: "row" | "page" = "page", order?: number) => (
    <CreoCard
      key={pickId(item)}
      item={item}
      variant={variant}
      order={order}
      disabled={!canUseWorkshop}
      onPick={() => start(item)}
      onInfo={() => {
        setChosen(item);
      }}
    />
  );

  return (
    <div className="ws-page ws-qc-page" data-layout={isPhone ? "phone" : "desk"}>
      <header className="ws-page-head ws-qc-head">
        <div className="ws-page-title-row">
          <h1 className="ws-page-title ws-page-title--xl">Creos</h1>
          <div className="ws-page-actions">
            <WallSearch value={query} onChange={setQuery} onSubmit={() => {}} label="Search Creos" />
          </div>
        </div>
        <p className="ws-page-sub">
          {q && list.data
            ? matches.length === 0
              ? `No results for ${q}`
              : `${matches.length} ${matches.length === 1 ? "result" : "results"} for ${q}`
            : isGuest
              ? `What Creo can make with you for ${brand}. ${tap} to add to your prompt; pick as many as you need.`
              : `What Creo can make for ${brand}, ready to start. ${tap} to add to your prompt; pick as many as you need.`}
        </p>
      </header>

      <div className="ws-qc-body">
        {list.isError && !list.data ? (
          <ErrorState
            className="ws-qc-state"
            title="Creos didn’t load"
            body="Your chats are fine. Try again in a moment."
            onRetry={() => void list.refetch()}
          />
        ) : list.isLoading ? (
          <>
            {[0, 1, 2].map((i) => (
              <section key={i} className="ws-qc-section" aria-hidden="true">
                <div className="ws-qc-section-head">
                  <span className="ws-skel ws-skel--title" style={{ width: 120 }} />
                </div>
                <ul className="ws-qc-grid">
                  {Array.from({ length: rowSize }, (_, j) => (
                    <CreoCardSkeleton key={j} variant="page" />
                  ))}
                </ul>
              </section>
            ))}
          </>
        ) : q && matches.length === 0 ? (
          <EmptyState
            className="ws-qc-state"
            icon={<SearchX aria-hidden />}
            title="Nothing called that yet"
            body="Try a different word, or ask Creo in a chat: it can do more than this list."
            actions={
              <KitButton variant="secondary" onClick={() => setQuery("")}>
                Clear search
              </KitButton>
            }
          />
        ) : (
          <>
            {streams.map((stream) => {
              const expanded = open.has(stream.key);
              // Search shows every match at once; otherwise the first row, and
              // the rest folded under it until See all opens them in place.
              const first = q ? stream.items : stream.items.slice(0, rowSize);
              const rest = q ? [] : stream.items.slice(rowSize);
              const more = rest.length > 0;
              return (
                <section key={stream.key} className="ws-qc-section" aria-label={stream.label}>
                  <div className="ws-qc-section-head">
                    <h2 className="ws-qc-section-title">{stream.label}</h2>
                    <span className="ws-qc-section-count">{stream.items.length}</span>
                    <span className="ws-qc-section-push" />
                    {more ? (
                      <button
                        type="button"
                        className="ws-qc-see-all"
                        aria-expanded={expanded}
                        onClick={() => toggle(stream.key)}
                      >
                        {expanded ? "Show fewer" : "See all"}
                        <ChevronDown aria-hidden="true" />
                      </button>
                    ) : null}
                  </div>
                  <ul className="ws-qc-grid">{first.map((item) => card(item))}</ul>
                  {more ? (
                    <div className="ws-qc-more" data-open={expanded || undefined} inert={!expanded}>
                      <div className="ws-qc-more-inner">
                        <ul className="ws-qc-grid">{rest.map((item, i) => card(item, "page", i))}</ul>
                      </div>
                    </div>
                  ) : null}
                </section>
              );
            })}
          </>
        )}
      </div>

      <WorkshopQuickCreateDetail
        item={chosen}
        onClose={() => setChosen(null)}
        projectId={desk.pickedProjectId}
        canWrite={canUseWorkshop}
        isPhone={isPhone}
        onUse={(item, projectId) => {
          setChosen(null);
          start(item, projectId);
        }}
      />
    </div>
  );
}
